// Documents tab (design/screens/patient-documents.html): the stored documents by type with their current / superseded state,
// upload from the camera or a file — resized on the device to at most 2 000 px and 2 MB, a WebP re-encoded as JPEG, a HEIC
// converted to JPEG where the browser can read it and refused with a note elsewhere (UC-PATIENTS-018, PAT-26, BR-PATIENTS-031) — open through the signed link the /url route answers with (UC-PATIENTS-019), and delete with
// the Control PIN step-up and a reason (UC-PATIENTS-020). Issued documents (invoices, receipts, prescriptions) arrive with
// the modules that issue them.
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_PX, PATIENT_DOCUMENT_TYPES, UPLOADABLE_DOCUMENT_TYPES, UPLOAD_CONTENT_TYPES, controlIdDisplay, type DocumentAccessAction, type InlineUpload, type PatientDocumentAccessEntry, type PatientDocumentType, type PatientDocumentView, type PatientView } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useRef, useState, type FormEvent, type ReactNode } from "react";
import { newIdempotencyKey } from "../api/client";
import { patientDocuments } from "../api/endpoints.patients";
import { describeError } from "../api/problem";
import { startDownload } from "../lib/download";
import { formatDate, formatDateTime, formatFileSize, formatTime } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { useWriteBlock } from "../shell/OfflineBanner";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Chip, Empty, ErrorAlert, Field, Loading, LockMark, Modal, Pager, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { CAPTURE_LABEL, DOC_TYPE_LABEL, useBranchNames, writeTitle } from "./PatientsShared";

const TYPE_ORDER = new Map(PATIENT_DOCUMENT_TYPES.map((t, i) => [t, i]));
const HEIC_NOTE = "This HEIC photo cannot be read on this device: set the camera to \"Most compatible\" (JPEG) on an iPhone or iPad, or export the photo as JPEG first.";
const HEIC_HELP = "A HEIC photo is converted to JPEG where this browser can read it (Safari on an iPhone, iPad or Mac); elsewhere set the camera to \"Most compatible\" (JPEG) or export the photo as JPEG first.";
const WEBP_NOTE = "This WebP image could not be converted on this device; save it as JPEG or PNG and upload that.";
/** `converted`: a WebP or HEIC re-encoded as JPEG on the device (BR-PATIENTS-031); `resized`: an image shrunk to the limits. */
export type PreparedUpload = { ok: true; file: InlineUpload; resized: boolean; converted: boolean } | { ok: false; error: string };

const isHeic = (f: File): boolean => /image\/hei[cf]/i.test(f.type) || /\.hei[cf]$/i.test(f.name);
const isWebp = (f: File): boolean => /^image\/webp$/i.test(f.type) || (!f.type && /\.webp$/i.test(f.name));
const contentTypeOf = (f: File): InlineUpload["contentType"] | null => {
  const t = f.type.toLowerCase();
  if ((UPLOAD_CONTENT_TYPES as readonly string[]).includes(t)) return t as InlineUpload["contentType"];
  if (!t && /\.jpe?g$/i.test(f.name)) return "image/jpeg";
  if (!t && /\.png$/i.test(f.name)) return "image/png";
  if (!t && /\.pdf$/i.test(f.name)) return "application/pdf";
  return null;
};
function toBase64(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes);
  let s = "";
  for (let i = 0; i < view.length; i += 0x8000) s += String.fromCharCode(...view.subarray(i, i + 0x8000));
  return btoa(s);
}
interface Decoded { width: number; height: number; source: CanvasImageSource; close: () => void }
/** An `<img>` loaded from the file, or null when the browser cannot read it. A browser always answers load or error. */
function loadImage(file: Blob): Promise<HTMLImageElement | null> {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function" || typeof Image === "undefined") return Promise.resolve(null);
  let url: string;
  try { url = URL.createObjectURL(file); } catch { return Promise.resolve(null); }
  return new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth > 0 ? img : null);
    img.onerror = () => resolve(null);
    img.src = url;
  }).finally(() => URL.revokeObjectURL(url));
}
/**
 * The image decoded for a canvas: `createImageBitmap` first, else an `<img>` — Safari reads a HEIC photo through either, which
 * is how it reaches a canvas there (BR-PATIENTS-031); null where the browser cannot read the file at all. Every browser the
 * PWA supports has `createImageBitmap`; without it (jsdom) nothing is decoded, since an `<img>` there never loads.
 */
async function decodeImage(file: Blob): Promise<Decoded | null> {
  if (typeof createImageBitmap !== "function") return null;
  try { const b = await createImageBitmap(file); return { width: b.width, height: b.height, source: b, close: () => b.close?.() }; } catch { /* the <img> may still read it */ }
  const img = await loadImage(file);
  return img ? { width: img.naturalWidth, height: img.naturalHeight, source: img, close: () => undefined } : null;
}
/** Draws the image onto a canvas so its longest edge is at most MAX_UPLOAD_PX and re-encodes it as JPEG under MAX_UPLOAD_BYTES; null where the browser cannot (no decoder, no canvas). */
async function shrinkImage(file: File): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const image = await decodeImage(file);
  if (!image) return null;
  try {
    const scale = Math.min(1, MAX_UPLOAD_PX / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(image.source, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.88, 0.8, 0.7, 0.6, 0.5]) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (blob && blob.size <= MAX_UPLOAD_BYTES) return blob;
    }
    return null;
  } finally { image.close(); }
}
/** A WebP or HEIC re-encoded as a JPEG inline upload named `.jpg`, or null where the device cannot read it (BR-PATIENTS-031). */
async function convertToJpeg(file: File): Promise<PreparedUpload | null> {
  const jpeg = await shrinkImage(file);
  if (!jpeg) return null;
  return { ok: true, file: { contentType: "image/jpeg", data: toBase64(await jpeg.arrayBuffer()), ...(file.name ? { fileName: `${file.name.replace(/\.[^.]+$/, "").slice(0, 196)}.jpg` } : {}) }, resized: false, converted: true };
}
async function imageTooLarge(file: File): Promise<boolean> {
  if (file.size > MAX_UPLOAD_BYTES) return true;
  if (typeof createImageBitmap !== "function") return false;
  try { const b = await createImageBitmap(file); const over = Math.max(b.width, b.height) > MAX_UPLOAD_PX; b.close?.(); return over; } catch { return false; }
}
/**
 * PAT-26 / BR-PATIENTS-012 and BR-PATIENTS-031 on the device: JPEG, PNG or PDF travel as they are; a WebP, and a HEIC where
 * the browser can read it, are re-encoded as JPEG (the Worker stores only the three); a HEIC it cannot read is refused with the
 * note; images shrunk to the limits, a PDF over 2 MB refused.
 */
export async function prepareUpload(file: File): Promise<PreparedUpload> {
  if (isHeic(file)) return (await convertToJpeg(file)) ?? { ok: false, error: HEIC_NOTE };
  if (isWebp(file)) return (await convertToJpeg(file)) ?? { ok: false, error: WEBP_NOTE };
  const contentType = contentTypeOf(file);
  if (!contentType) return { ok: false, error: `${file.type || file.name} is not accepted — a JPEG, PNG or PDF is.` };
  let blob: Blob = file;
  let resized = false;
  let outType = contentType;
  if (contentType === "application/pdf") {
    if (file.size > MAX_UPLOAD_BYTES) return { ok: false, error: `The PDF is ${(file.size / 1_048_576).toFixed(1)} MB; at most 2 MB is stored. Scan at a lower resolution.` };
  } else if (await imageTooLarge(file)) {
    const shrunk = await shrinkImage(file);
    if (!shrunk) return { ok: false, error: "The image is over 2 MB or 2 000 px and could not be resized on this device; save it smaller and try again." };
    blob = shrunk;
    resized = true;
    outType = "image/jpeg";
  }
  const data = toBase64(await blob.arrayBuffer());
  return { ok: true, file: { contentType: outType, data, ...(file.name ? { fileName: file.name.replace(/\.[^.]+$/, resized ? ".jpg" : "$&").slice(0, 200) } : {}) }, resized, converted: false };
}

export function PatientsDocuments({ p }: { p: PatientView }) {
  const sess = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const withStepUp = useStepUp();
  const docs = useQuery({ queryKey: ["patient", p.id, "documents"], queryFn: () => patientDocuments.list(p.id, { includeSuperseded: "true" }) });
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const [deleting, setDeleting] = useState<PatientDocumentView | null>(null);
  const rows = [...(docs.data?.data.items ?? [])].sort((a, b) => (TYPE_ORDER.get(a.type)! - TYPE_ORDER.get(b.type)!) || (Number(b.isCurrent) - Number(a.isCurrent)) || b.capturedAt.localeCompare(a.capturedAt));
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["patient", p.id] }); };
  /** UC-PATIENTS-020: the reason is taken in the modal, which closes before the Control PIN prompt so one dialog owns the keyboard. */
  const remove = (d: PatientDocumentView, reason: string) => {
    setDeleting(null);
    setNotice(null);
    const key = newIdempotencyKey();
    void withStepUp((g) => patientDocuments.remove(p.id, d.id, reason, { ...g, idempotencyKey: key }), { title: `Delete ${DOC_TYPE_LABEL[d.type]}`, description: `The document is taken out of use for ${p.firstNames} ${p.surname} (${p.accountCode}); the file is retained under the retention policy.`, reason: "none" })
      .then((r) => { if (!r) { setNotice(<Alert tone="warn"><b>Cancelled.</b> The document is unchanged.</Alert>); return; } refresh(); setNotice(<Alert tone="ok"><b>{DOC_TYPE_LABEL[d.type]} deleted.</b> Recorded in the Control Log; the file is retained under the retention policy.</Alert>); })
      .catch((err: unknown) => setNotice(<Alert tone="crit"><b>Not deleted.</b> {describeError(err)}</Alert>));
  };
  /** UC-PATIENTS-019: View shows the file in a new tab; Download saves it from this page, which never leaves the app (BR-PATIENTS-030). */
  const open = (d: PatientDocumentView) => { window.open(patientDocuments.urlPath(p.id, d.id), "_blank", "noopener"); };
  const download = (d: PatientDocumentView) => { startDownload(patientDocuments.downloadPath(p.id, d.id), ""); };
  const stateOf = (d: PatientDocumentView): ReactNode => d.voidedAt ? <Chip tone="crit">Deleted</Chip> : !d.isCurrent ? <Chip tone="neutral">Superseded{d.supersededAt ? ` ${formatDate(d.supersededAt)}` : ""}</Chip> : d.flaggedOutdatedAt ? <Chip tone="warn">Possibly outdated</Chip> : <Chip tone="ok" dot>Current</Chip>;
  return (
    <div className="split wide">
      <div className="stack" style={{ gap: 16 }}>
        {notice}
        {docs.isPending && <Loading what="documents" />}
        {docs.isError && <ErrorAlert error={docs.error} />}
        {docs.data && (
          <Panel title="Stored documents" meta={`${rows.filter((r) => r.isCurrent && !r.voidedAt).length} current · scans, photos and consents`} tight foot={<span className="help">View shows a document in a new tab; Download saves it. Either is logged against your Control ID, and the link it opens through expires after 15 minutes.</span>}>
            {rows.length === 0 ? <Empty title="No documents on file.">{can(sess, "patients.document.write") ? "Upload the ID scan, the medical-aid card and a photo with the Upload panel." : ""}</Empty> : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Document</th><th>Captured</th><th>Source</th><th>State</th><th className="r"></th></tr></thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.id}>
                        <td><span className="strong">{DOC_TYPE_LABEL[d.type]}</span>{d.description && <div className="small faint">{d.description}</div>}<div className="small faint">{d.contentType}{d.widthPx && d.heightPx ? ` · ${d.widthPx} × ${d.heightPx} px` : d.pageCount ? ` · ${d.pageCount} page${d.pageCount === 1 ? "" : "s"}` : ""} · {formatFileSize(d.sizeBytes)}</div></td>
                        <td className="nw">{formatDate(d.capturedAt)}{d.capturedBy && <div className="small faint">{d.capturedBy.displayName}</div>}</td>
                        <td>{CAPTURE_LABEL[d.captureMethod]}</td>
                        <td>{stateOf(d)}</td>
                        <td className="r"><div className="acts">
                          {!d.voidedAt && can(sess, "patients.document.read") && <button className="btn sm" type="button" onClick={() => open(d)}>View</button>}
                          {!d.voidedAt && can(sess, "patients.document.read") && <button className="btn sm" type="button" onClick={() => download(d)}>Download</button>}
                          {!d.voidedAt && can(sess, "patients.document.delete") && <button className="btn sm danger" type="button" disabled={!online} title={writeTitle(block)} onClick={() => setDeleting(d)}>Delete<LockMark /></button>}
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        <Panel title="Issued documents" meta="invoices, receipts, quotes, lab orders and prescriptions">
          <span className="help">Generated documents appear here as the modules that issue them land (Sprints 3, 5 and 6); a reprint is a copy of the original, marked “Reprint” and logged.</span>
        </Panel>
      </div>
      <div className="stack" style={{ gap: 16 }}>
        {can(sess, "patients.document.write") && <UploadPanel p={p} online={online} title={writeTitle(block)} onUploaded={(d) => { refresh(); setNotice(<Alert tone="ok"><b>{DOC_TYPE_LABEL[d.type]} uploaded.</b>{d.supersededByDocumentId === null && d.isCurrent ? " It is the current version of its type." : ""}</Alert>); }} />}
        {can(sess, "governance.access_log.read") && <AccessPanel p={p} />}
        <Panel title="Retention">
          <dl className="kv">
            <dt>Policy</dt><dd>Set under Settings › General</dd>
            <dt>Storage</dt><dd>Private, per practice · files open through short-lived links</dd>
            <dt>Deletion</dt><dd>Needs a Control PIN and a reason; the file is retained, the document leaves every list</dd>
          </dl>
        </Panel>
      </div>
      {deleting && <DeleteModal doc={deleting} onClose={() => setDeleting(null)} onConfirm={(reason) => remove(deleting, reason)} />}
    </div>
  );
}

/** A tablet or phone — a touch screen — whose camera a file input with `capture` opens (PAT-19, PAT-26). A desk computer has none. */
const touchDevice = (): boolean => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(any-pointer: coarse)").matches;

function UploadPanel({ p, online, title, onUploaded }: { p: PatientView; online: boolean; title: string | undefined; onUploaded: (d: PatientDocumentView) => void }) {
  const [type, setType] = useState<PatientDocumentType>("IdDocument");
  const [description, setDescription] = useState("");
  // The file and how it was captured: Take photo is `Camera`, the file control `Upload` (BR-PATIENTS-031).
  const [picked, setPicked] = useState<{ file: File; method: "Camera" | "Upload" } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [touch] = useState(touchDevice);
  const input = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const pick = (file: File | undefined, method: "Camera" | "Upload") => {
    setPicked(file ? { file, method } : null);
    setError(null);
    // One source at a time: a photo replaces a chosen file, and a chosen file a photo.
    const other = method === "Camera" ? input.current : camera.current;
    if (file && other) other.value = "";
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    try {
      const prepared = await prepareUpload(picked.file);
      if (!prepared.ok) { setError(prepared.error); return; }
      const { data } = await patientDocuments.upload(p.id, { type, file: prepared.file, captureMethod: picked.method, ...(description.trim() ? { description: description.trim() } : {}) }, newIdempotencyKey());
      setPicked(null);
      setDescription("");
      for (const el of [input.current, camera.current]) if (el) el.value = "";
      onUploaded(data);
    } catch (err) { setError(describeError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="Upload" meta="camera or file · JPEG, PNG or PDF" foot={<span className="help">Images are resized on the device to at most 2 000 px and 2 MB before upload, and a WebP or HEIC is converted to JPEG. A new document of a type supersedes the current one, which stays readable.</span>}>
      <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
        {error && <Alert tone="crit"><b>Not uploaded.</b> {error}</Alert>}
        <Field label="Type" required htmlFor="doc-type"><Select id="doc-type" value={type} onChange={(e) => setType(e.target.value as PatientDocumentType)}>{UPLOADABLE_DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{DOC_TYPE_LABEL[t]}</option>)}</Select></Field>
        {touch && (
          <div className="field">
            <input ref={camera} type="file" accept="image/*" capture="environment" hidden aria-hidden="true" tabIndex={-1} data-testid="doc-camera" onChange={(e) => pick(e.target.files?.[0], "Camera")} />
            <div className="row c"><button className="btn" type="button" onClick={() => camera.current?.click()}><Icon name="camera" />Take photo</button><span className="help">Opens the camera; or choose a file below.</span></div>
          </div>
        )}
        <Field label="File" required htmlFor="doc-file" help={HEIC_HELP}><input id="doc-file" ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf" onChange={(e) => pick(e.target.files?.[0], "Upload")} /></Field>
        <Field label="Description" htmlFor="doc-desc"><TextInput id="doc-desc" value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} placeholder="Smart ID card, scanned at the front desk" /></Field>
        <div className="row c"><button className="btn primary" type="submit" disabled={!picked || busy || !online} title={title}><Icon name="plus" />{busy ? "Uploading…" : "Upload"}</button>{picked && <span className="small faint">{picked.method === "Camera" ? "Photo" : picked.file.name} · {formatFileSize(picked.file.size)}</span>}</div>
      </form>
    </Panel>
  );
}

const ACCESS_VERB: Record<DocumentAccessAction, string> = { View: "viewed", Download: "downloaded", Print: "printed", Send: "sent" };
/**
 * UC-PATIENTS-026 (design/screens/patient-documents.html, "Access today"): who opened which document, newest first, by day —
 * for an Owner or Admin, the roles that read the access logs. Reading it writes nothing but the record's own access entry.
 */
function AccessPanel({ p }: { p: PatientView }) {
  const { name: branchName } = useBranchNames();
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  const q = useQuery({ queryKey: ["patient", p.id, "document-access", cursor ?? ""], queryFn: () => patientDocuments.access(p.id, { cursor }) });
  const rows = q.data?.data.items ?? [];
  const days = new Map<string, PatientDocumentAccessEntry[]>();
  for (const r of rows) { const day = formatDate(r.accessedAt); days.set(day, [...(days.get(day) ?? []), r]); }
  return (
    <Panel title="Document access" meta="who opened which document · newest first" tight foot={<>
      <span className="help">Every View and Download is logged with the link it opened through.</span>
      {q.data && (cursors.length > 0 || q.data.data.nextCursor) && <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!q.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, q.data!.data.nextCursor!])} />}
    </>}>
      {q.isPending && <div className="panel-body"><Loading what="document access" /></div>}
      {q.isError && <div className="panel-body"><ErrorAlert error={q.error} /></div>}
      {q.data && rows.length === 0 && <Empty title="No document has been opened yet." />}
      {rows.length > 0 && (
        <div className="list">
          {[...days.entries()].map(([day, entries]) => (
            <Fragment key={day}>
              <div className="day">{day}</div>
              {entries.map((r) => (
                <div key={r.id} className="item">
                  <span className="t">{formatTime(r.accessedAt)}</span>
                  <div className="grow">
                    <div className="primary">{DOC_TYPE_LABEL[r.documentType]} {ACCESS_VERB[r.action]}{r.channel ? ` by ${r.channel}` : ""}</div>
                    <div className="sub">{r.user ? <>{r.user.displayName} <span className="mono">{controlIdDisplay(r.user.controlId)}</span></> : "Vision X"}{r.branchId && branchName(r.branchId) ? ` · ${branchName(r.branchId)}` : ""}</div>
                  </div>
                </div>
              ))}
            </Fragment>
          ))}
        </div>
      )}
    </Panel>
  );
}

/** UC-PATIENTS-020: the reason first; the Control PIN (`Delete`, record `patient_document`) follows once this closes. */
function DeleteModal({ doc, onClose, onConfirm }: { doc: PatientDocumentView; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title={`Delete ${DOC_TYPE_LABEL[doc.type]}`} meta={`captured ${formatDateTime(doc.capturedAt)}`} onClose={onClose} onSubmit={() => { if (reason.trim()) onConfirm(reason.trim()); }} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={!reason.trim()}>Delete<LockMark /></button>
    </>}>
      <Alert tone="warn"><b>The type has no current document afterwards</b> until a new one is uploaded; an older superseded version is not restored.</Alert>
      <Field label="Reason" required htmlFor="doc-delete-reason" help="Written to the Control Log with the authorising Control ID"><TextInput id="doc-delete-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
    </Modal>
  );
}
